import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/** Profile headline: the app's own font, set a little tighter. */
export const HEADLINE_SX = {
  letterSpacing: '-0.01em',
  color: 'text.secondary',
  fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
};

export const initialsOf = (name) =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

/** The lightest tone of the profile card's halo: the activity feed's background (one tint, not another grey). */
export const HALO_TINT = '#f5f6fd';           // #667eea at about 5% over white

/** The brand gradient of the activity feed's reload button (#667eea → #764ba2). */
export const RING_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
// One seamless loop of the two brand colours: the purple part blends into the blue on both sides
// (no hard edge, no lighter patch), and sweeps around as the ring turns
const RING_ARC = 'conic-gradient(from 0deg, rgba(102,126,234,0.45) 0deg, rgba(108,98,214,0.5) 90deg, '
  + 'rgba(118,75,162,0.55) 180deg, rgba(108,98,214,0.5) 270deg, rgba(102,126,234,0.45) 360deg)';

/**
 * Avatar circled by the reload button's gradient ring, which periodically spins like the button.
 * `children` are overlaid (e.g. a camera button).
 */
export function RingAvatar({ src, name, size = 88, ring = 2, onClick, children, sx }) {
  const theme = useTheme();
  return (
    <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 0, ...sx }}>
      <Box
        aria-hidden
        sx={{
          // Fixed square, so the ring stays a true circle whatever else the wrapper holds
          position: 'absolute',
          top: 0,
          left: 0,
          width: size + 2 * ring,
          height: size + 2 * ring,
          borderRadius: '50%',
          background: RING_ARC,
          // Out-glow in the ring's own colours (centred, so it stays even while the ring turns)
          boxShadow: `0 0 10px 1px ${alpha('#667eea', 0.3)}, 0 0 22px 4px ${alpha('#764ba2', 0.15)}`,
          // Two quick revolutions, then a rest, like the reload button spinning while it refreshes
          animation: 'otableRingTurn 5s cubic-bezier(0.45, 0, 0.25, 1) infinite',
          '@keyframes otableRingTurn': {
            '0%': { transform: 'rotate(0deg)' },
            '36%': { transform: 'rotate(720deg)' },
            '100%': { transform: 'rotate(720deg)' },
          },
        }}
      />
      <Avatar
        src={src || undefined}
        alt={name}
        onClick={onClick}
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          m: `${ring}px`,
          width: size,
          height: size,
          cursor: onClick ? 'pointer' : 'default',
          border: `3px solid ${theme.palette.background.paper}`,
          background: RING_GRADIENT,
          color: '#fff',
          fontSize: size * 0.36,
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        {initialsOf(name)}
      </Avatar>
      {children}
    </Box>
  );
}

/** Connections / Following (organisations, calls, labs…) / Consortia as three cells; a null count (hidden by
 *  privacy) is left out. LINKS: {connections, following, consortia} → the page each cell opens. */
export function NetworkCounts({ connections, following, consortia, links = {}, sx }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const cells = [
    { key: 'connections', label: connections === 1 ? 'Connection' : 'Connections', value: connections },
    { key: 'following', label: 'Following', value: following },
    { key: 'consortia', label: consortia === 1 ? 'Consortium' : 'Consortia', value: consortia },
  ].filter((c) => c.value !== null && c.value !== undefined);
  if (!cells.length) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
        py: 0.5,
        ...sx,
      }}
    >
      {cells.map((c, i) => (
        <Box
          key={c.key}
          {...(links[c.key] && { role: 'link', tabIndex: 0, onClick: () => navigate(links[c.key]),
            onKeyDown: (e) => { if (e.key === 'Enter') navigate(links[c.key]); } })}
          sx={{
            textAlign: 'center',
            minWidth: 0,
            borderLeft: i ? `1px solid ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
            // a link without a hover background: the number takes the brand colour
            ...(links[c.key] && { cursor: 'pointer', '&:hover .count, &:focus-visible .count': { color: 'primary.main' },
              '&:focus-visible': { outline: 'none' } }),
          }}
        >
          <Typography className="count" sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums',
            transition: 'color .15s' }}>
            {c.value}
          </Typography>
          <Typography noWrap sx={{ fontSize: '.9rem', color: 'text.secondary' }}>{c.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}
